import React, { useEffect, useState } from 'react';
import { Col, Container, Dropdown, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import ActionMenu from '../../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../../components/ActionMenu/ActionMenuItem';
import Button from '../../../components/Buttons/Button';
import DataTable from '../../../components/DataTable/DataTable';
import { IColInfo } from '../../../components/DataTable/Interfaces';
import ButtonBar from '../../../components/Fields/ButtonBar';
import DateField from '../../../components/Fields/DateField';
import Form from '../../../components/Fields/Form';
import TextareaControl from '../../../components/Fields/TextareaControl';
import TextareaField from '../../../components/Fields/TextareaField';
import TextboxField from '../../../components/Fields/TextboxField';
import { FieldConfig, FormConfig, ValidatorConfig } from '../../../components/Fields/Types';
import Title from '../../../components/Headers/Title';
import useValidation from '../../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from "../../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../../contexts/authContext';
import { useNotifications } from '../../../contexts/notificationContext';
import { useTranslations } from '../../../contexts/translationContext';
import CommentModel from '../../../models/CommentModel';
import FileCreateResultModel from '../../../models/FileCreateResultModel';
import ItemRevisionColorModel from '../../../models/ItemRevisionColorModel';
import WorkflowActionModel from '../../../models/WorkflowActionModel';
import WorkflowTaskModel from '../../../models/WorkflowTaskModel';
import WorkflowTaskQueryModel from '../../../models/WorkflowTaskQueryModel';
import DataService from '../../../services/data.service';
import { Result } from '../../../types/Result';
import FormatUtils from '../../../utils/FormatUtils';
import Unauthorized from '../../Authentication/Unauthorized';
import CommentView from '../../Items/Components/Comment';
import ItemColorControl from '../../Items/Components/ItemColorControl';
import Task_popup_ReAssign from '../Popups/Task_popup_ReAssign';
import Task_popup_ResumeWorkflow from '../Popups/Task_popup_ResumeWorkflow';
import Task_popup_StopWorkflow from '../Popups/Task_popup_StopWorkflow';
import Task_popup_SuspendWorkflow from '../Popups/Task_popup_SuspendWorkflow';
import TaskAddBusinessPartnerView from '../Tasks/TaskAddBusinessPartner';
import TaskAddDocumentView from '../Tasks/TaskAddDocument';
import TaskAddPriceView from '../Tasks/TaskAddPrice';
import TaskEditItemView from '../Tasks/TaskEditItem';
import TaskEditItemRevisionDatesView from '../Tasks/TaskEditItemRevisionDates';
import TaskEditPricesView from '../Tasks/TaskEditPrices';
import TaskReviseDocumentView from '../Tasks/TaskReviseDocument';
import TaskSetCustomFieldsView from '../Tasks/TaskSetCustomFields';
import TaskSetProjectItemCustomFieldsView from '../Tasks/TaskSetProjectItemCustomFields';

export interface IProps {
    taskId: number
    onTaskSubmitted: (newTaskId?: number) => void
}

const TaskView: React.FC<IProps> = ({ taskId, onTaskSubmitted }) => {
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [formData, setFormData] = useState<WorkflowTaskModel>();
    const [itemRevisionColors, setItemRevisionColors] = useState<ItemRevisionColorModel[]>([]);
    const [previousTasks, setPreviousTasks] = useState<WorkflowTaskQueryModel[]>()
    const [addDocumentFiles, setAddDocumentFile] = useState<File[]>();
    const [reviseDocumentFile, setReviseDocumentFile] = useState<File>();
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const [comments, setComments] = useState<string>('')

    const [showReAssignPopup, setShowReAssignPopup] = useState<boolean>(false);
    const [showResumeWorkflowPopup, setShowResumeWorkflowPopup] = useState<boolean>(false);
    const [showStopWorkflowPopup, setShowStopWorkflowPopup] = useState<boolean>(false);
    const [showSuspendWorkflowPopup, setShowSuspendWorkflowPopup] = useState<boolean>(false);

    const { handleResult, handleException, notify } = useNotifications();
    const { user, isAuthorizedForWorkflowDefinition } = useAuth();
    const navigate = useNavigate()
    const formatUtils = new FormatUtils()

    useEffect(() => {
        if (taskId) {
            setShowSpinner(true);
            DataService.getById<WorkflowTaskModel>('api/v1/workflowtasks/', taskId)
                .then(response => {
                    setShowSpinner(false);
                    console.log('formData', response)
                    setFormData({ ...response });

                    var colors = response.workflow.subItemRevisions.map(i => i.itemRevisionColor)
                    setItemRevisionColors(colors)

                    if (response.workflow.workflowStatusId == 2)
                        notify("info", "The workflow is suspended.");
                    else if (response.workflow.workflowStatusId == 4)
                        notify("info", "The workflow is stopped.");
                    else if (response.workflow.workflowStatusId == 5)
                        notify("info", "The workflow is finished.");

                    if (response.completedOn)
                        notify("info", "The task is completed.");

                })
                .catch(e => {
                    setShowSpinner(false);
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [taskId, refreshCounter]);

    useEffect(() => {
        loadTasks();
    }, [formData?.workflowId, taskId, refreshCounter])

    function loadTasks() {
        if (formData?.workflowId) {
            setShowSpinner(true)
            DataService.get<WorkflowTaskQueryModel[]>('api/v1/WorkflowTasks/CompletedByMainWorkflow/' + formData.workflow.mainWorkflowId)
                .then(response => {
                    setShowSpinner(false)
                    setPreviousTasks(response);
                })
                .catch(e => { handleException(e); });
        }
    }

    async function submit() {
        console.log('submit');
        if (formData) {
            if (formData.workflowTaskAddDocumentModel && addDocumentFiles) {
                setShowSpinner(true);

                let fileResults: FileCreateResultModel[] = []
                await Promise.all(addDocumentFiles.map(async (addDocumentFile) => {
                    let fileResponse = await DataService.createFile(addDocumentFile)
                    if (fileResponse.isSuccesful === true && fileResponse.data) {
                        fileResults.push(fileResponse.data)
                    }
                }))

                formData.workflowTaskAddDocumentModel.fileResults = fileResults;
                submitWorkflowTask(formData);

            }
            else if (formData.workflowTaskReviseDocumentModel && reviseDocumentFile) {
                setShowSpinner(true);
                DataService
                    .createFile(reviseDocumentFile)
                    .then(fileResponse => {
                        setShowSpinner(false);
                        handleResult(fileResponse);
                        if (formData && formData.workflowTaskReviseDocumentModel && fileResponse.isSuccesful === true && fileResponse.data) {
                            formData.workflowTaskReviseDocumentModel.fileResult = fileResponse.data;
                            submitWorkflowTask(formData);
                        }
                    })
                    .catch(e => { setShowSpinner(false); handleException(e); });

            }
            else {
                submitWorkflowTask(formData);
            }
        }
    }

    function submitWorkflowTask(model: WorkflowTaskModel) {
        console.log('submitworkflowtask');
        setShowSpinner(true);
        DataService
            .update<number>(`api/v1/workflowtasks/`, model)
            .then(response => {
                setShowSpinner(false);
                handleResult(response);
                setServerErrors(response);
                if (response.isSuccesful === true) {
                    if (onTaskSubmitted)
                        onTaskSubmitted(response.data);

                    setRefreshCounter(x => x + 1)
                }
            })
            .catch(e => { setShowSpinner(false); handleException(e); });
    }

    function loadComments() {
        if (formData) {
            setShowSpinner(true);
            DataService.get<CommentModel[]>('api/v1/comments/ByWorkflow/' + formData.workflow.mainWorkflowId)
                .then(response => {
                    setShowSpinner(false);
                    formData.commentModels = response
                    setFormData(x => ({ ...formData }));
                })
                .catch(e => { setShowSpinner(false); handleException(e); });
        }
    }

    function submitComments() {
        setShowSpinner(true);
        if (formData && comments != '') {
            const data = {
                discussionId: formData.discussionId,
                workflowId: formData.workflow.mainWorkflowId,
                text: comments,
            };
            DataService
                .create<Result>(`api/v1/comments/`, data)
                .then(response => {
                    setShowSpinner(false);
                    handleResult(response);
                    if (response.isSuccesful) {
                        setComments('')
                        loadComments()
                    }
                })
                .catch(e => {
                    setShowSpinner(false);
                    handleException(e);
                });
        }
    }

    function setFieldValue(name: string, value: any) {
        if (formData) {
            if (name === 'workflowTaskAddDocumentModel.document') {
                if (!value.length)
                    value = [value];

                setAddDocumentFile(value);
            }
            else if (name === 'workflowTaskReviseDocumentModel.document' && typeof (value) === 'object') {
                setReviseDocumentFile(value);
            }
            else {
                name.split('.').reduce((object, propertyName, index, elements) => {
                    if (index === elements.length - 1) {
                        object[propertyName] = value;
                        setFormData(formData);
                    }

                    if (object && object[propertyName]) {
                        return object[propertyName];
                    }
                    return null;
                }, formData);
            }
        }
    }

    function onPriceCellChange(value: any, rowIndex: number, dataProp?: string) {
        if (formData && formData.workflowTaskEditPricesModel && dataProp) {
            dataProp.split('.').reduce((object, propertyName, index, elements) => {
                if (index === elements.length - 1) {
                    object[propertyName] = value;
                    setFormData({ ...formData });
                }

                if (object && object[propertyName]) {
                    return object[propertyName];
                }
                return null;
            }, formData.workflowTaskEditPricesModel.prices[rowIndex]);
        }
    }

    function onItemRevsionDatesCellChange(value: any, rowIndex: number, dataProp?: string) {
        // console.log('cell change', value, rowIndex, dataProp)
        if (formData && formData.workflowTaskEditItemRevisionDatesModel && dataProp) {
            dataProp.split('.').reduce((object, propertyName, index, elements) => {
                if (index === elements.length - 1) {
                    object[propertyName] = value;
                    setFormData({ ...formData });
                }

                if (object && object[propertyName]) {
                    return object[propertyName];
                }
                return null;
            }, formData.workflowTaskEditItemRevisionDatesModel.itemRevisions[rowIndex]);
        }
    }

    function onProjectItemCustomFieldsCellChange(value: any, rowIndex: number, dataProp?: string) {
        // console.log('cell change', value, rowIndex, dataProp)
        if (formData && formData.workflowTaskSetProjectItemCustomFieldsModel && dataProp) {
            dataProp.split('.').reduce((object, propertyName, index, elements) => {
                if (index === elements.length - 1) {
                    object[propertyName] = value;
                    setFormData({ ...formData });
                }

                if (object && object[propertyName]) {
                    return object[propertyName];
                }
                return null;
            }, formData.workflowTaskSetProjectItemCustomFieldsModel.projectItemRevisions[rowIndex]);
        }
    }


    var businessPartnerLabel = 'Business-partner'
    if (formData && formData.workflowTaskAddBusinessPartnerModel)
        businessPartnerLabel = formData.workflowTaskAddBusinessPartnerModel.businessPartnerType.name;

    var addDocumentlabel = 'Document'
    var addDocumentValidationRules: ValidatorConfig[] = []
    if (formData && formData.workflowTaskAddDocumentModel) {
        addDocumentlabel = formData.workflowTaskAddDocumentModel.attachmentType.name;
        if (formData.workflowTaskAddDocumentModel.attachmentRequired)
            addDocumentValidationRules = [{ type: 'isRequired' }]
    }

    var reviseDocumentlabel = 'Document'
    var reviseDocumentValidationRules: ValidatorConfig[] = []
    if (formData && formData.workflowTaskReviseDocumentModel) {
        reviseDocumentlabel = formData.workflowTaskReviseDocumentModel.attachmentType.name;
        if (formData.workflowTaskReviseDocumentModel.attachmentRequired)
            reviseDocumentValidationRules = [{ type: 'isRequired' }]
    }

    let fields: FieldConfig[] = [
        { name: 'workflow.itemRevision.item.name', label: 'Item', labelSize: 2 },
        { name: 'workflow.itemRevision.item.itemNr', label: 'Item', labelSize: 4 },
        { name: 'workflow.project.name', label: 'Project', labelSize: 2 },
        { name: 'workflow.project.id', label: 'Project', labelSize: 4 },
        { name: 'comments', label: 'Task comments' },
        { name: 'createdOn', label: 'Created on', labelSize: 4 },
        { name: 'assignedToUser.name', label: 'Assigned to', labelSize: 4 },
        { name: 'assignedOn', label: 'Assigned on', labelSize: 4 },
        { name: 'dueOn', label: 'Due on', labelSize: 4 },
        { name: 'workflowTaskAddBusinessPartnerModel.businessPartnerId', label: businessPartnerLabel },
        { name: 'workflowTaskAddBusinessPartnerModel.contactPersonId', label: 'Contactperson' },
        { name: 'workflowTaskAddDocumentModel.document', label: addDocumentlabel, validationRules: addDocumentValidationRules },
        { name: 'workflowTaskReviseDocumentModel.attachmentId', label: 'Document to revise', validationRules: reviseDocumentValidationRules },
        { name: 'workflowTaskReviseDocumentModel.document', label: reviseDocumentlabel, validationRules: reviseDocumentValidationRules },
        { name: 'workflowTaskAddPriceModel.price.priceTypeId', label: 'Pricetype' },
        { name: 'workflowTaskAddPriceModel.price.businessPartnerId', label: 'Business-partner' },
        { name: 'workflowTaskAddPriceModel.price.priceTrancheTypeId', label: 'Tranche type' },
        { name: 'workflowTaskAddPriceModel.price.priceTrancheId', label: 'Tranche' },
        { name: 'workflowTaskAddPriceModel.price.currencyId', label: 'Currency' },
        { name: 'workflowTaskAddPriceModel.price.amount', label: 'Price' },
        { name: 'workflowTaskAddPriceModel.price.incoTermId', label: 'Pricetype' },
        { name: 'workflowTaskAddPriceModel.price.beginDate', label: 'Active from' },
        { name: 'workflowTaskAddPriceModel.price.endDate', label: 'Until' },
        { name: 'workflowTaskAddPriceModel.price.remarks', label: 'Remarks' },
        { name: 'workflowTaskEditItemModel.itemName', label: 'Name' },
        { name: 'workflowTaskEditItemModel.itemDescription', label: 'Description' },
    ];

    if (formData && formData.workflowTaskSetCustomFieldsModel && formData.workflowTaskSetCustomFieldsModel.userDefinedFields) {
        formData.workflowTaskSetCustomFieldsModel.userDefinedFields.forEach((value, index) => {
            fields.push({ name: `workflowTaskSetCustomFieldsModel.userDefinedFields.${index}.value`, label: value.userDefinedField.name });
        });
    }
    /*
    if (formData && formData.milestones) {
        formData.milestones.forEach((value, index) => {
            fields.push({ name: 'milestones.' + index, label: value.milestoneName, labelSize: 5 });
        });
    }
    */

    const formConfig: FormConfig = {
        fields: fields,
        handleSubmit: submit,
        handleFieldChanged: setFieldValue,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    if (!isAuthorized)
        return (<Unauthorized />);

    if (!formData)
        return <SpinnerOverlay />;

    /*
        var allOtherTasksDone = true
        formData.workflowStep.workflowTasks.forEach(task => {
            if (task.id != formData.id && task.taskDefinition.isRequired && !task.isDone)
                allOtherTasksDone = false;
        })
    */

    var nextActions: WorkflowActionModel[] = []
    if (!formData.isDone) {
        nextActions = formData.nextWorkflowActions;
    }

    async function NextAction(chosenWorkflowActionId?: number) {
        console.log('Next step: ' + chosenWorkflowActionId);
        if (formData) {
            formData.chosenWorkflowActionId = chosenWorkflowActionId;
            //setFormData(formData);
            await submit();
        }
    };

    function gotoItem() {
        if (formData?.workflow?.itemRevision) {
            const itemRevision = formData.workflow.itemRevision
            let url = '/plm/Items/ItemRevision/' + itemRevision.id;
            let navigateTitle = itemRevision.item.itemNr + ' - v' + itemRevision.revisionNumber;
            navigate(url, { state: { title: navigateTitle, fromMenu: false } });
        }
    }

    function gotoProject() {
        if (formData?.workflow?.project) {
            const project = formData.workflow.project
            let url = '/plm/Projects/Details/' + project.id;
            let navigateTitle = 'Project ' + project.id;
            navigate(url, { state: { title: navigateTitle, fromMenu: false } });
        }
    }

    function gotoWorkflow() {
        if (formData?.workflow) {
            const workflow = formData.workflow;
            let url = '/plm/workflowsetup/workflow/' + workflow.workflowDefinitionId;
            let navigateTitle = 'Workflow definition ' + workflow.id;
            navigate(url, { state: { title: navigateTitle, fromMenu: false } });
        }
    }

    function gotoStep() {
        if (formData) {
            let url = '/plm/workflowsetup/workflowstep/' + formData.workflowStepDefinitionId;
            let navigateTitle = 'Workflow step definition ' + formData.workflowStep;
            navigate(url, { state: { title: navigateTitle, fromMenu: false } });
        }
    }

    function undoTask() {
        if (formData) {
            setShowSpinner(true);
            DataService
                .post<Result>('api/v1/WorkflowTasks/' + taskId + '/Undo', undefined)
                .then(response => {
                    setShowSpinner(false);
                    handleResult(response);
                    setRefreshCounter((counter) => counter + 1);
                })
                .catch(e => { setShowSpinner(false); handleException(e); });
        }
    }

    function assignToMe() {
        if (formData) {
            setShowSpinner(true);
            DataService
                .post<Result>('api/v1/WorkflowTasks/' + taskId + '/AssignToMe', undefined)
                .then(response => {
                    setShowSpinner(false);
                    handleResult(response);
                    setRefreshCounter((counter) => counter + 1);
                })
                .catch(e => { setShowSpinner(false); handleException(e); });
        }
    }

    const formActions =
        <ActionMenu title="Actions">
            {formData.workflow.project && <ActionMenuItem width={400} icon="fa fa-link" title={{ format: 'Project: {0}', args: [formData.workflow.project.name] }} onClick={() => { gotoProject(); }} />}
            {formData.workflow.itemRevision && <ActionMenuItem width={400} icon="fa fa-link" title={{ format: 'Item: {0}', args: [formData.workflow.itemRevision.item.itemNr + ' V' + formData.workflow.itemRevision.revisionNumber] }} onClick={() => { gotoItem(); }} />}
            <Dropdown.Divider />
            {formData.isLastCompletedTask == true && <ActionMenuItem icon="fa fa-undo" title="Undo task" appFunction={ApplicationFunctions.UndoWorkflowTask} workflowDefinitionId={formData.workflow.workflowDefinitionId} onClick={() => { undoTask(); }} />}
            {!formData.completedOn && <>
                {formData.assignedToUserId != user?.userId && <ActionMenuItem icon="fa fa-hand-o-down" title="Assign to me" appFunction={ApplicationFunctions.AssignWorkflowTaskToMe} workflowDefinitionId={formData.workflow.workflowDefinitionId} onClick={() => { assignToMe(); }} />}
                <ActionMenuItem icon="fa fa-hand-o-right" title="Reassign task" appFunction={ApplicationFunctions.ReAssignWorkflowTask} workflowDefinitionId={formData.workflow.workflowDefinitionId} onClick={() => { setShowReAssignPopup(true); }} />
                <Dropdown.Divider />
            </>}
            {formData.workflow.workflowStatusId == 1 && <ActionMenuItem icon="fa fa-pause" title="Suspend workflow" appFunction={ApplicationFunctions.SuspendWorkflow} workflowDefinitionId={formData.workflow.workflowDefinitionId} onClick={() => { setShowSuspendWorkflowPopup(true); }} />}
            {formData.workflow.workflowStatusId == 2 && <ActionMenuItem icon="fa fa-play" title="Resume workflow" appFunction={ApplicationFunctions.SuspendWorkflow} workflowDefinitionId={formData.workflow.workflowDefinitionId} onClick={() => { setShowResumeWorkflowPopup(true); }} />}
            {formData.workflow.workflowStatusId == 1 && <ActionMenuItem icon="fa fa-stop" title="Stop workflow" appFunction={ApplicationFunctions.StopWorkflow} workflowDefinitionId={formData.workflow.workflowDefinitionId} onClick={() => { setShowStopWorkflowPopup(true); }} />}
            <Dropdown.Divider />
            <ActionMenuItem icon="fa fa-hand-o-right" title="Edit workflow" appFunction={ApplicationFunctions.ViewWorkflowDefinition} workflowDefinitionId={formData.workflow.workflowDefinitionId} onClick={() => { gotoWorkflow(); }} />
            <ActionMenuItem icon="fa fa-hand-o-right" title="Edit Step" appFunction={ApplicationFunctions.ViewWorkflowDefinition} workflowDefinitionId={formData.workflow.workflowDefinitionId} onClick={() => { gotoStep(); }} />
        </ActionMenu>

    function getCellData(rowData: WorkflowTaskQueryModel, colInfo: IColInfo) {
        switch (colInfo.title) {
            case 'When':
                return rowData.completedOn ?? rowData.dueOn;
            case 'Who':
                return rowData.completedByUser ?? rowData.assignedToUser;

            default: return '';
        }
    }

    function getStatusColor(rowData: WorkflowTaskQueryModel, colInfo: IColInfo): string | undefined {
        const now = new Date(Date.now())
        const dueOn = formatUtils.dateFromString(rowData.dueOn);
        const completedOn = rowData.completedOn
            ? formatUtils.dateFromString(rowData.completedOn)
            : undefined;

        if (dueOn === undefined || dueOn >= now || (completedOn && completedOn <= dueOn))
            return 'Green';

        return 'red';
    }

    return (
        <div className='main-content'>
            <Form title={formData.title} actions={formActions} footer={(
                <>
                    {formData.workflow.workflowStatusId == 1 && (
                        <Row>
                            <Col md='12'>
                                <ButtonBar offset={1}>
                                    {(isAuthorizedForWorkflowDefinition(ApplicationFunctions.ExecuteAllWorkflowTasks, formData.workflow.workflowDefinitionId) ||
                                        (formData.assignedToUserId === user?.userId && isAuthorizedForWorkflowDefinition(ApplicationFunctions.ExecuteMyWorkflowTasks, formData.workflow.workflowDefinitionId))) && (
                                            <>
                                                {
                                                    nextActions.map((workflowAction, index) => (
                                                        <Button text={workflowAction.name} type='button' onClick={async () => await NextAction(workflowAction.id)} />
                                                    ))
                                                }
                                                {!formData.isDone && nextActions.length == 0 &&
                                                    <Button text={'Done'} type='button' onClick={async () => await NextAction()} />
                                                }
                                            </>)}
                                </ButtonBar>
                            </Col>
                        </Row>
                    )}
                </>
            )}>
                <div>
                    <Row>
                        <Col md='6'>
                            <div style={{ fontSize: '16px', fontWeight: 700, margin: '0px 0px 10px', padding: 10, minHeight: 250, border: '2px solid #333', borderRadius: 4, backgroundColor: 'lightgray', marginTop: 6 }} className="caption row">{formData.taskDefinition.description}</div>
                            {itemRevisionColors.map(color => <ItemColorControl itemColor={color} onClick={() => { }} />)}
                            {formData.workflowTaskAddBusinessPartnerModel && (<TaskAddBusinessPartnerView formData={formData.workflowTaskAddBusinessPartnerModel} getFieldProps={getFieldProps} />)}
                            {formData.workflowTaskAddDocumentModel && (<TaskAddDocumentView formData={formData.workflowTaskAddDocumentModel} getFieldProps={getFieldProps} />)}
                            {formData.workflowTaskReviseDocumentModel && (<TaskReviseDocumentView formData={formData.workflowTaskReviseDocumentModel} workflowId={formData.workflowId} getFieldProps={getFieldProps} />)}
                            {formData.workflowTaskAddPriceModel && (<TaskAddPriceView formData={formData.workflowTaskAddPriceModel} getLabelProps={getLabelProps} getControlProps={getControlProps} getFieldProps={getFieldProps} workflow={formData.workflow} />)}
                            {formData.workflowTaskEditItemModel && (<TaskEditItemView formData={formData.workflowTaskEditItemModel} getFieldProps={getFieldProps} />)}
                            {formData.workflowTaskEditItemRevisionDatesModel && (<TaskEditItemRevisionDatesView formData={formData.workflowTaskEditItemRevisionDatesModel} onCellChange={onItemRevsionDatesCellChange} />)}
                            {formData.workflowTaskEditPricesModel && (<TaskEditPricesView formData={formData.workflowTaskEditPricesModel} onCellChange={onPriceCellChange} workflow={formData.workflow} />)}
                            {formData.workflowTaskSetCustomFieldsModel && (<TaskSetCustomFieldsView formData={formData.workflowTaskSetCustomFieldsModel} getFieldProps={getFieldProps} />)}
                            {formData.workflowTaskSetProjectItemCustomFieldsModel && (<TaskSetProjectItemCustomFieldsView formData={formData.workflowTaskSetProjectItemCustomFieldsModel} onCellChange={onProjectItemCustomFieldsCellChange} />)}
                            <Title text='Task comments' size={4} />
                            <div style={{ border: '1px solid black'}}><TextareaControl {...getControlProps('comments')} cols={80} rows={6} /></div>
                        </Col>
                        <Col md='6'>
                            <Row>
                                <Col md='12'>
                                    {formData.workflow.itemRevision && <TextboxField {...getFieldProps('workflow.itemRevision.item.name')} disabled />}
                                    {formData.workflow.project && <TextboxField {...getFieldProps('workflow.project.name')} disabled />}
                                </Col>
                            </Row>
                            <Row>
                                <Col md='6'>
                                    <Row>
                                        {formData.workflow.itemRevision &&
                                            <Col md='6' onClick={gotoItem}>
                                                <a href={'/plm/Items/ItemRevision/' + formData.workflow.itemRevision.id}>{formData.workflow.itemRevision.item.itemNr}</a>
                                                <img src={formData.workflow.itemRevision.thumbnailUrl} style={{ width: '100%', marginBottom: 8 }} />
                                            </Col>
                                        }
                                        {formData.workflow.project &&
                                            <Col md='6' onClick={gotoProject}>
                                                <a href={'/plm/Projects/Details/' + formData.workflow.project.id}>{'Project: ' + formData.workflow.project.id}</a>
                                                <img src={formData.workflow.project.thumbnailUrl} style={{ width: '100%', marginBottom: 8 }} />
                                            </Col>
                                        }
                                    </Row>
                                    <Row>
                                        <Col md='12'>
                                            <DateField {...getFieldProps('createdOn')} disabled />
                                            <TextboxField {...getFieldProps('assignedToUser.name')} disabled />
                                            <DateField {...getFieldProps('assignedOn')} disabled />
                                            <DateField {...getFieldProps('dueOn')} disabled />
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md='6'>
                                    <Title text='WF comments' size={4} />
                                    <div>
                                        <div style={{ border: '1px solid #e3e3e3', borderRadius: 10, marginBottom: 3 }}>
                                            <div style={{ backgroundColor: '#e3e3e3', borderTopLeftRadius: 10, borderTopRightRadius: 10, height: 40, borderBottomColor: 'black' }} >
                                                <div className='user' style={{ padding: 5 }}><div className='thumbnail' style={{}}><img height='32' src={user?.thumbnailUrl} /></div></div>
                                                <div className='pull-left' style={{ paddingLeft: 5 }}>{user?.name}</div>
                                            </div>
                                            <div style={{ margin: 5 }}>
                                                <TextareaControl name='comments' value={comments} onChange={(value) => setComments(value)} validationResults={[]} />
                                            </div>
                                        </div>
                                        <div>
                                            <Button type='button' text='Submit' onClick={() => submitComments()}></Button>
                                        </div>
                                    </div>
                                    <div style={{ marginTop: 12 }}>
                                        {formData && formData.commentModels &&
                                            formData.commentModels.map((value, index) => {
                                                return <CommentView comment={value} key={index} />
                                            })
                                        }
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            </Form>
            <DataTable
                name='dtTasks'
                tableData={previousTasks}
                refreshNr={refreshCounter}
                navigateTitle='Task'
                navigateUrl='/plm/Workflow/task/'
                colInfos={[
                    { key: true, dataProp: 'id', visible: false },
                    { getCellBackgroundColor: getStatusColor, sort: false, width: 5 },
                    { title: 'When', dataType: 'datetime', getCellData: getCellData, sort: 'desc' },
                    { title: 'Who', dataType: 'user', getCellData: getCellData },
                    { title: 'Step', dataProp: 'workflowStep' },
                    { title: 'Task', dataProp: 'task' },
                    { title: 'Chosen Action', dataProp: 'chosenWorkflowAction' },
                    { title: 'Next Action', dataProp: 'nextAction' },
                    { title: 'Comments', dataProp: 'comments' },
                ]}
            />
            {taskId && showReAssignPopup && <Task_popup_ReAssign taskId={taskId} show={showReAssignPopup} onHide={() => { setShowReAssignPopup(false); setRefreshCounter(refreshCounter + 1); }} />}
            {showResumeWorkflowPopup && <Task_popup_ResumeWorkflow workflowId={formData.workflowId} show={showResumeWorkflowPopup} onHide={() => { setShowResumeWorkflowPopup(false); setRefreshCounter(refreshCounter + 1); }} />}
            {showStopWorkflowPopup && <Task_popup_StopWorkflow workflowId={formData.workflowId} show={showStopWorkflowPopup} onHide={() => { setShowStopWorkflowPopup(false); setRefreshCounter(refreshCounter + 1); }} />}
            {showSuspendWorkflowPopup && <Task_popup_SuspendWorkflow workflowId={formData.workflowId} show={showSuspendWorkflowPopup} onHide={() => { setShowSuspendWorkflowPopup(false); setRefreshCounter(refreshCounter + 1); }} />}
            {showSpinner && <SpinnerOverlay />}
        </div>
    )

}
export default TaskView;